/**
 * What the Jobs list can be asked for: search, filters, the When filter, the
 * summary cards' quick filters, sorting and paging — kept in the address.
 */
import { z } from 'zod'
import { recordIdSchema } from '../shared/record-id.ts'
import { billingStates } from './billing.ts'
import { shownStatuses } from './job.ts'

export const jobSortKeys = [
  'number',
  'when',
  'job',
  'cleaner',
  'status',
  'amount',
  'billing',
  'notice',
] as const
export type JobSortKey = (typeof jobSortKeys)[number]

export const whenFilters = ['today', 'week', 'next7', 'past', 'date', 'range'] as const
export type WhenFilter = (typeof whenFilters)[number]

export const quickFilters = ['today', 'week', 'ready'] as const
export type QuickFilter = (typeof quickFilters)[number]

/** One page of the list, and everything that narrowed it. */
export const jobsQuerySchema = z.object({
  q: z.string().trim().max(200).default(''),
  /** A summary card pressed. */
  quick: z.enum(quickFilters).nullable().default(null),
  status: z.array(z.enum(shownStatuses)).default([]),
  clients: z.array(recordIdSchema).default([]),
  locations: z.array(recordIdSchema).default([]),
  cleaners: z.array(recordIdSchema).default([]),
  workTypes: z.array(recordIdSchema).default([]),
  billing: z.array(z.enum(billingStates)).default([]),
  when: z.enum(whenFilters).nullable().default(null),
  /** For `when: 'date'` (from only) and `when: 'range'`. */
  from: z.iso.date().nullable().default(null),
  to: z.iso.date().nullable().default(null),
  /** The client page's Jobs tab: only this client's jobs. */
  clientId: recordIdSchema.nullable().default(null),
  sort: z
    .object({ key: z.enum(jobSortKeys), dir: z.enum(['asc', 'desc']) })
    .nullable()
    .default(null),
  page: z.number().int().min(0).default(0),
  pageSize: z.number().int().min(1).max(100).default(12),
})
export type JobsQuery = z.infer<typeof jobsQuerySchema>
export type JobsQueryInput = z.input<typeof jobsQuerySchema>
